import { supabase } from "@/integrations/supabase/client";
import { ApiCallsAuditService } from "@/services/apiCallsAuditService";
import { LLMConfigService } from "@/services/llm/LLMConfigService";
import { ApiTokensService } from "@/hooks/api-tokens/service";
import { UnifiedLLMService } from "@/services/llm/UnifiedLLMService";

interface ChatCompletionParams {
  apiKey: string;
  model: string;
  messages: Array<{ role: string; content: string }>;
  source: string;
  brandId?: string;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  skipConfigCheck?: boolean; // Flag to skip config check when called from UnifiedLLMService
}

interface LovableAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
    finish_reason: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export class LovableAIWrapper {
  private readonly endpoint = 'https://ai.gateway.lovable.dev/v1/chat/completions';
  private readonly maxRetries = 3;

  async chatCompletion(params: ChatCompletionParams): Promise<LovableAIResponse> {
    const {
      apiKey: providedApiKey,
      model: providedModel,
      messages,
      source,
      brandId,
      temperature: providedTemperature,
      max_tokens: providedMaxTokens,
      stream = false,
      skipConfigCheck = false
    } = params;

    // Check for LLM configuration for this source
    let finalModel = providedModel;
    let finalApiKey = providedApiKey;
    let finalTemperature = providedTemperature;
    let finalMaxTokens = providedMaxTokens;
    let configUsed = false;

    try {
      // Skip config check if called from UnifiedLLMService (to avoid infinite loops)
      if (skipConfigCheck) {
        console.log(`[LovableAIWrapper] Skipping config check (called from UnifiedLLMService)`);
      } else {
        const config = await LLMConfigService.getLLMConfig(brandId || '', source);
        console.log(`[LovableAIWrapper] Checking config for source: "${source}", brandId: "${brandId || 'none'}"`);
        console.log(`[LovableAIWrapper] Config lookup result:`, config ? {
          provider: config.provider,
          model: config.model,
          isActive: config.isActive,
          priority: config.priority
        } : 'No config found');
        
        if (config && config.isActive) {
          if (config.provider === 'lovable') {
            // Config exists for Lovable - use it
            finalModel = config.model;
            configUsed = true;
            
            // Get API key from tokens (Lovable uses gemini key or lovable key)
            const apiTokens = await ApiTokensService.fetchTokens(brandId || '');
            if ((apiTokens as any).lovable) {
              finalApiKey = (apiTokens as any).lovable;
            } else if (apiTokens.gemini) {
              // Fallback to gemini key if lovable key not available
              finalApiKey = apiTokens.gemini;
            }
            
            // Merge metadata from config
            if (config.metadata) {
              if (config.metadata.temperature !== undefined) {
                finalTemperature = providedTemperature ?? config.metadata.temperature;
              }
              if (config.metadata.max_tokens !== undefined) {
                finalMaxTokens = providedMaxTokens ?? config.metadata.max_tokens;
              }
            }
            
            console.log(`[LovableAIWrapper] ✅ Using configured LLM for source "${source}": ${config.provider}/${config.model}`);
          } else {
            // Config exists but for a different provider - route through UnifiedLLMService
            console.log(`[LovableAIWrapper] 🔄 Config exists for source "${source}" with provider "${config.provider}", routing through UnifiedLLMService for provider switch`);
            
            // Convert messages to UnifiedLLMService format and route
            const unifiedResponse = await UnifiedLLMService.callLLM({
              brandId: brandId || '',
              source,
              messages: messages.map(msg => ({
                role: msg.role,
                content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)
              })),
              temperature: providedTemperature,
              max_tokens: providedMaxTokens,
              stream
            });
            
            console.log(`[LovableAIWrapper] ✅ UnifiedLLMService returned response from ${unifiedResponse.provider}/${unifiedResponse.model}`);
            
            // Convert UnifiedLLMService response back to LovableAI format
            return {
              choices: [{
                message: {
                  content: unifiedResponse.content
                },
                finish_reason: unifiedResponse.finish_reason || 'stop'
              }],
              usage: unifiedResponse.usage
            };
          }
        } else {
          console.log(`[LovableAIWrapper] No active config found, using provided values: ${providedModel}`);
        }
      }
    } catch (error) {
      // If config lookup fails, fall back to provided values
      console.warn('[LovableAIWrapper] Failed to fetch LLM config, using provided values:', error);
    }

    const startTime = Date.now();
    let lastError: any;

    // Retry loop with exponential backoff
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        const response = await this.makeRequest({
          apiKey: finalApiKey,
          model: finalModel,
          messages,
          temperature: finalTemperature,
          max_tokens: finalMaxTokens,
          stream
        });

        const statusCode = response.status;

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error?.message || response.statusText;

          // Log the error
          await this.logApiCall({
            source,
            brandId,
            model: finalModel,
            statusCode,
            duration: Date.now() - startTime,
            tokens: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
            metadata: {
              error: errorMessage,
              request_failed: true,
              attempt,
              messages_count: messages.length,
              temperature: finalTemperature,
              max_tokens: finalMaxTokens,
              config_used: configUsed
            }
          });

          // Handle specific error codes
          if (statusCode === 429) {
            throw new Error('Rate limit exceeded. Please try again in a moment.');
          }
          if (statusCode === 402) {
            throw new Error('AI credits exhausted. Please add credits to continue.');
          }

          throw new Error(`Lovable AI error (${statusCode}): ${errorMessage}`);
        }

        const responseData: LovableAIResponse = await response.json();

        // Extract token usage
        let promptTokens = 0;
        let completionTokens = 0;
        let totalTokens = 0;
        let estimatedTokens = false;

        if (responseData.usage) {
          promptTokens = responseData.usage.prompt_tokens;
          completionTokens = responseData.usage.completion_tokens;
          totalTokens = responseData.usage.total_tokens;
        } else {
          // Estimate tokens if not provided
          const content = responseData.choices[0]?.message?.content || '';
          completionTokens = Math.ceil(content.length / 4);
          promptTokens = Math.ceil(JSON.stringify(messages).length / 4);
          totalTokens = promptTokens + completionTokens;
          estimatedTokens = true;
        }

        // Determine platform based on model prefix
        const platform = finalModel.startsWith('google/') ? 'gemini' : 
                        finalModel.startsWith('openai/') ? 'openai' : 'gemini';

        // Calculate cost
        const costEstimate = ApiCallsAuditService.estimateCost(
          platform,
          { prompt_tokens: promptTokens, completion_tokens: completionTokens, total_tokens: totalTokens },
          finalModel
        );

        // Log successful call
        await this.logApiCall({
          source,
          brandId,
          model: finalModel,
          statusCode,
          duration: Date.now() - startTime,
          tokens: {
            prompt_tokens: promptTokens,
            completion_tokens: completionTokens,
            total_tokens: totalTokens,
            estimated_tokens: estimatedTokens
          },
          costEstimate,
          metadata: {
            messages_count: messages.length,
            temperature: finalTemperature,
            max_tokens: finalMaxTokens,
            finish_reason: responseData.choices[0]?.finish_reason,
            estimated_tokens: estimatedTokens,
            platform,
            config_used: configUsed
          }
        });

        return responseData;

      } catch (error) {
        lastError = error;

        // Don't retry on last attempt or for specific errors
        if (attempt === this.maxRetries || 
            (error instanceof Error && (
              error.message.includes('credits exhausted') || 
              error.message.includes('Rate limit')
            ))) {
          break;
        }

        // Exponential backoff: 2s, 4s, 8s
        const delayMs = Math.pow(2, attempt) * 1000;
        console.log(`Retry attempt ${attempt} failed, waiting ${delayMs}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    // If we've exhausted all retries, log final failure
    if (!lastError) {
      lastError = new Error('Request failed after all retries');
    }

    await this.logApiCall({
      source,
      brandId,
      model: finalModel,
      statusCode: 0,
      duration: Date.now() - startTime,
      tokens: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
      metadata: {
        error: lastError instanceof Error ? lastError.message : 'Unknown error',
        request_failed: true,
        all_retries_exhausted: true,
        messages_count: messages.length,
        temperature: finalTemperature,
        max_tokens: finalMaxTokens,
        config_used: configUsed
      }
    });

    throw lastError;
  }

  private async makeRequest(params: {
    apiKey: string;
    model: string;
    messages: Array<{ role: string; content: string }>;
    temperature?: number;
    max_tokens?: number;
    stream?: boolean;
  }): Promise<Response> {
    const { apiKey, model, messages, temperature, max_tokens, stream } = params;

    const requestBody: any = {
      model,
      messages,
      stream
    };

    if (temperature !== undefined) {
      requestBody.temperature = temperature;
    }

    if (max_tokens !== undefined) {
      requestBody.max_tokens = max_tokens;
    }

    return fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
  }

  private async logApiCall(data: {
    source: string;
    brandId?: string;
    model: string;
    statusCode: number;
    duration: number;
    tokens: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
      estimated_tokens?: boolean;
    };
    costEstimate?: number;
    metadata?: any;
  }) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      // Determine platform based on model prefix
      const platform = data.model?.startsWith('google/') ? 'gemini' : 
                      data.model?.startsWith('openai/') ? 'openai' : 'gemini';

      await ApiCallsAuditService.logApiCall({
        source_menu_item: data.source,
        platform,
        endpoint: this.endpoint,
        method: 'POST',
        status_code: data.statusCode,
        duration_ms: data.duration,
        cost_estimate: data.costEstimate || 0,
        brand_id: data.brandId,
        model_used: data.model,
        metadata: {
          ...data.metadata,
          prompt_tokens: data.tokens.prompt_tokens,
          completion_tokens: data.tokens.completion_tokens,
          total_tokens: data.tokens.total_tokens
        }
      });
    } catch (error) {
      console.error('Failed to log API call:', error);
    }
  }
}
